// WebMCP integration for lolahealth.com storefront.
//
// Exposes a small set of real, no-auth storefront actions to AI agents that
// visit the page with a browser implementing the WebMCP draft
// (navigator.modelContext).
//
// Spec: https://webmachinelearning.github.io/webmcp/
// Chrome flag: chrome://flags/#enable-web-mcp

(function () {
  'use strict';

  if (typeof navigator === 'undefined' || !navigator.modelContext || typeof navigator.modelContext.provideContext !== 'function') {
    return;
  }

  const tools = [
    {
      name: 'search_products',
      description:
        'Search the Lola Health catalog for blood tests, supplements, or other products by free-text query. Returns up to 10 matches with handle, title, price, and product URL.',
      inputSchema: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Free-text search query, e.g. "cortisol test" or "NMN".' },
          limit: { type: 'integer', minimum: 1, maximum: 10, default: 5 },
        },
        required: ['query'],
      },
      async execute({ query, limit }) {
        const resource = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${Math.min(limit || 5, 10)}&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor`;
        const res = await fetch(resource, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`Search failed with ${res.status}`);
        const data = await res.json();
        const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        return {
          query,
          count: products.length,
          products: products.map((p) => ({
            title: p.title,
            handle: p.handle,
            url: p.url,
            price: p.price,
            vendor: p.vendor,
            available: p.available,
          })),
        };
      },
    },
    {
      name: 'get_cart',
      description: 'Return the current contents of the shopping cart for this browser session.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      async execute() {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`Cart fetch failed with ${res.status}`);
        const cart = await res.json();
        return {
          item_count: cart.item_count,
          total_price: cart.total_price,
          currency: cart.currency,
          items: (cart.items || []).map((i) => ({
            title: i.product_title,
            variant_title: i.variant_title,
            quantity: i.quantity,
            line_price: i.final_line_price,
            url: i.url,
            variant_id: i.variant_id,
          })),
        };
      },
    },
    {
      name: 'add_to_cart',
      description:
        'Add a product variant to the cart by numeric variant id. Use search_products first to find a variant id. Requires the user to be on lolahealth.com.',
      inputSchema: {
        type: 'object',
        properties: {
          variant_id: {
            type: 'integer',
            description: 'Shopify variant id (numeric). Can be obtained from search_products results or a product page URL (?variant=...).',
          },
          quantity: { type: 'integer', minimum: 1, maximum: 10, default: 1 },
        },
        required: ['variant_id'],
      },
      async execute({ variant_id, quantity }) {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variant_id, quantity: quantity || 1 }),
        });
        if (!res.ok) {
          const detail = await res.text().catch(() => '');
          throw new Error(`Add to cart failed with ${res.status}: ${detail}`);
        }
        return await res.json();
      },
    },
    {
      name: 'find_a_clinic',
      description:
        'Open the Lola Health clinic finder so the user can locate a partner phlebotomy clinic near them. Returns the URL the user should visit.',
      inputSchema: {
        type: 'object',
        properties: {
          postcode: { type: 'string', description: 'Optional UK postcode or city to pre-fill the finder.' },
        },
      },
      async execute({ postcode }) {
        const base = 'https://lolahealth.com/pages/find-a-clinic';
        const url = postcode ? `${base}?q=${encodeURIComponent(postcode)}` : base;
        return { url, note: 'Direct the user to open this URL. The clinic list is rendered client-side on an interactive map.' };
      },
    },
  ];

  try {
    navigator.modelContext.provideContext({ tools });
  } catch (err) {
    if (window.console && console.warn) console.warn('[webmcp] provideContext failed:', err);
  }
})();
